import React from 'react';
import { 
  Clock
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const AnalysisTimeline = ({ review, platform, metadata }) => {

  // Create timeline graph data with commentary
  // Function to get short labels from criteria names
  const getShortLabel = (criteriaName) => {
    // Special case for brand safety compliance
    if (criteriaName.toLowerCase().includes('safety') || criteriaName.toLowerCase().includes('compliance')) {
      return 'Safety';
    }
    // Use first word for all other criteria
    return criteriaName.split(' ')[0];
  };

  const getTimelineGraphData = () => {
    const data = [];
    
    // Extract points from criteria only
    const criteria = review?.criteria || review?.videoAnalysis?.criteria || [];
    criteria.forEach((criterion, index) => {
      const shortLabel = getShortLabel(criterion.name);
      
      const scoreType = criterion.score >= 8 ? 'positive' : criterion.score < 5 ? 'negative' : 'neutral';
      const commentary = criterion.explanation || `${criterion.name} scored ${criterion.score}/10`;
      
      data.push({
        time: shortLabel,
        timeSeconds: index, // Use index for sorting
        score: criterion.score,
        type: scoreType,
        name: criterion.name,
        commentary: commentary
      });
    });

    return data.sort((a, b) => a.timeSeconds - b.timeSeconds);
  };

  const timelineData = getTimelineGraphData();
  
  // Calculate average score from criteria
  const criteria = review?.criteria || review?.videoAnalysis?.criteria || [];
  const averageScore = criteria.length > 0 
    ? (criteria.reduce((sum, criterion) => sum + criterion.score, 0) / criteria.length).toFixed(1)
    : '0.0';
  
  // Determine status based on average score
  const getStatusLabel = (score) => {
    const numScore = parseFloat(score);
    if (numScore >= 8) return 'Acceptable';
    if (numScore >= 5) return 'Needs Improvement';
    return 'Critical';
  };
  
  const statusLabel = getStatusLabel(averageScore);
  const statusColor = parseFloat(averageScore) >= 8 ? 'text-green-800' : parseFloat(averageScore) >= 5 ? 'text-amber-800' : 'text-red-800';
  
  const getBarColor = (type) => {
    switch (type) {
      case 'positive': return '#10b981'; // green
      case 'negative': return '#ef4444'; // red
      case 'neutral': return '#f59e0b'; // amber
      default: return '#6b7280'; // gray
    }
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg max-w-xs">
          <div className="flex items-center mb-2">
            <div 
              className="w-3 h-3 rounded-full mr-2" 
              style={{ backgroundColor: getBarColor(data.type) }}
            ></div>
            <p className="font-semibold text-gray-900">{data.name}</p>
          </div>
          <p className="text-sm text-gray-600 mb-2">
            <span className="font-medium">Score:</span> {data.score}/10
          </p>
          <p className="text-sm text-gray-700">
            {data.commentary}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-primary-600" />
              Average Score: {averageScore}/10
            </h3>
            <span className={`ml-3 inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
              parseFloat(averageScore) >= 8 ? 'bg-green-100 text-green-800' :
              parseFloat(averageScore) >= 5 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
            }`}>
              {statusLabel}
            </span>
          </div>
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              <span className="text-gray-600">Acceptable</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-amber-500 rounded-full mr-2"></div>
              <span className="text-gray-600">Needs Improvement</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
              <span className="text-gray-600">Critical</span>
            </div>
          </div>
        </div>

        {/* Timeline Graph */}
        <div className="h-96 mb-6">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={timelineData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis 
                dataKey="time" 
                stroke="#6b7280"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                domain={[0, 10]}
                stroke="#6b7280"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                label={{ value: 'Score (1-10)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="score" 
                radius={[4, 4, 0, 0]}
                shape={(props) => {
                  const { payload, fill, x, y, width, height, ...restProps } = props;
                  return (
                    <rect
                      x={x}
                      y={y}
                      width={width}
                      height={height}
                      fill={getBarColor(payload.type)}
                    />
                  );
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

      </div>
    </div>
  );
};

export default AnalysisTimeline;

